import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Trash2, ArrowLeft, Mail, Building2, Clock, Search, Loader2, LogOut, Shield, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(null); // null=checking, false=not auth, true=auth
  const [pin, setPin] = useState("");
  const [contacts, setContacts] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [activeTab, setActiveTab] = useState("contacts");
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);

  const getAuthHeader = () => {
    const token = sessionStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Check existing session on mount
  useEffect(() => {
    const token = sessionStorage.getItem("admin_token");
    if (!token) {
      setAuthenticated(false);
      return;
    }
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthenticated(true))
      .catch(() => {
        sessionStorage.removeItem("admin_token");
        setAuthenticated(false);
      });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/admin/auth`, { pin });
      sessionStorage.setItem("admin_token", res.data.token);
      setAuthenticated(true);
      toast.success("Access granted.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid access code.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setAuthenticated(false);
    setContacts([]);
    setSelectedContact(null);
    setPin("");
  };

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, assessmentsRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers: getAuthHeader() }),
        axios.get(`${API}/assessment/submissions`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setContacts(contactsRes.data);
      setAssessments(assessmentsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setAuthenticated(false);
        toast.error("Session expired. Please re-authenticate.");
      } else {
        toast.error("Failed to load contacts.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeader() });
      toast.success("Contact deleted.");
      setContacts((prev) => prev.filter((c) => c.id !== id));
      if (selectedContact?.id === id) setSelectedContact(null);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setAuthenticated(false);
      }
      toast.error("Failed to delete contact.");
    }
  };

  useEffect(() => {
    if (authenticated) fetchContacts();
  }, [authenticated, fetchContacts]);

  const filtered = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.organization.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // Loading state
  if (authenticated === null) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  // PIN Gate
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: "#0A1325", border: "1px solid #1E293B", color: "#F8FAFC" },
          }}
        />
        <div className="w-full max-w-sm">
          <button
            data-testid="admin-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </button>
          <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-3">
            Administration
          </p>
          <h1
            data-testid="admin-login-heading"
            className="font-serif text-3xl font-bold text-white tracking-tight mb-8"
          >
            Secure Access
          </h1>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                Access Code
              </label>
              <input
                data-testid="admin-pin-input"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full bg-transparent border-b border-slate-700 text-white pb-3 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
                placeholder="Enter admin access code"
                autoFocus
              />
            </div>
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={authLoading}
              className="w-full border border-[#D4AF37] text-[#D4AF37] py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#050B14] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Authenticate"}
            </button>
          </form>
          <p className="text-[10px] text-slate-700 mt-8 text-center">
            Protected by server-side verification with brute force protection.
          </p>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#050B14]">
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: "#0A1325", border: "1px solid #1E293B", color: "#F8FAFC" },
        }}
      />

      {/* Top Bar */}
      <div className="border-b border-slate-800 bg-[#050B14]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-home-btn"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="font-serif text-lg font-bold text-white">JCAM</span>
            <span className="text-xs tracking-[0.15em] uppercase text-slate-500 hidden sm:block">
              Admin Console
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span data-testid="admin-contact-count" className="text-xs text-slate-500">
              {contacts.length} submissions
            </span>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs text-slate-500 hover:text-white transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Tabs */}
        <div className="flex items-center gap-6 mb-8 border-b border-slate-800 pb-4">
          <button
            data-testid="admin-tab-contacts"
            onClick={() => { setActiveTab("contacts"); setSearch(""); }}
            className={`flex items-center gap-2 text-xs tracking-[0.15em] uppercase transition-colors pb-2 -mb-[18px] ${
              activeTab === "contacts"
                ? "text-[#D4AF37] border-b border-[#D4AF37]"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <Mail className="w-3.5 h-3.5" />
            Contacts ({contacts.length})
          </button>
          <button
            data-testid="admin-tab-assessments"
            onClick={() => { setActiveTab("assessments"); setSearch(""); }}
            className={`flex items-center gap-2 text-xs tracking-[0.15em] uppercase transition-colors pb-2 -mb-[18px] ${
              activeTab === "assessments"
                ? "text-[#D4AF37] border-b border-[#D4AF37]"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Assessments ({assessments.length})
          </button>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="admin-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={activeTab === "contacts" ? "Search contacts..." : "Search assessments..."}
              className="w-full bg-transparent border-b border-slate-700 text-white pb-3 pl-7 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-slate-600"
            />
          </div>
        </div>

        {activeTab === "assessments" ? (
          <AssessmentsView assessments={assessments} search={search} loading={loading} />
        ) : (
        <>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500 text-sm">
              {search ? "No contacts match your search." : "No contact submissions yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Contact List */}
            <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-2">
              {filtered.map((contact) => (
                <button
                  key={contact.id}
                  data-testid={`contact-row-${contact.id}`}
                  onClick={() => setSelectedContact(contact)}
                  className={`w-full text-left p-4 border transition-colors duration-200 ${
                    selectedContact?.id === contact.id
                      ? "border-[#D4AF37]/50 bg-[#0F1A33]"
                      : "border-slate-800 bg-[#0A1325] hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white font-medium truncate">
                      {contact.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{contact.email}</span>
                  </div>
                  {contact.organization && (
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                      <Building2 className="w-3 h-3" />
                      <span className="truncate">{contact.organization}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-2">
              {selectedContact ? (
                <div
                  data-testid="contact-detail-panel"
                  className="border border-slate-800 bg-[#0A1325] p-8"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <h2 className="font-serif text-2xl text-white mb-1">
                        {selectedContact.name}
                      </h2>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5" />
                          {selectedContact.email}
                        </span>
                        {selectedContact.organization && (
                          <span className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5" />
                            {selectedContact.organization}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid="contact-delete-btn"
                      onClick={() => deleteContact(selectedContact.id)}
                      className="flex items-center gap-2 text-xs text-red-400 hover:text-red-300 border border-red-400/30 hover:border-red-400/60 px-4 py-2 transition-colors duration-300"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>

                  <div className="mb-6">
                    <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">
                      Message
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                      {selectedContact.message}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500 border-t border-slate-800 pt-4">
                    <Clock className="w-3 h-3" />
                    <span>Received {formatDate(selectedContact.created_at)}</span>
                  </div>
                </div>
              ) : (
                <div className="border border-slate-800 bg-[#0A1325] p-8 flex items-center justify-center min-h-[300px]">
                  <p className="text-sm text-slate-500">
                    Select a contact to view details.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

function AssessmentsView({ assessments, search, loading }) {
  const filtered = assessments.filter(
    (a) =>
      (a.lead_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.lead_email || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.lead_organization || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.tier || "").toLowerCase().includes(search.toLowerCase())
  );

  const tierColors = {
    "Q-Day Hardened": "text-emerald-400",
    "Prepared": "text-[#D4AF37]",
    "Moderate Risk": "text-amber-400",
    "High Risk": "text-red-400",
    "Critical Risk": "text-red-500",
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500 text-sm">
          {search ? "No assessments match your search." : "No assessment submissions yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header row */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 text-[10px] tracking-[0.12em] uppercase text-slate-600">
        <div className="col-span-3">Name</div>
        <div className="col-span-3">Organization</div>
        <div className="col-span-2">Score</div>
        <div className="col-span-2">Tier</div>
        <div className="col-span-2">Date</div>
      </div>
      {filtered.map((a) => (
        <div
          key={a.id}
          data-testid={`assessment-row-${a.id}`}
          className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 border border-slate-800 bg-[#0A1325] p-4 hover:border-slate-700 transition-colors"
        >
          <div className="col-span-3">
            <p className="text-sm text-white font-medium">{a.lead_name}</p>
            <p className="text-xs text-slate-500">{a.lead_email}</p>
          </div>
          <div className="col-span-3 flex items-center">
            <p className="text-xs text-slate-400">{a.lead_organization || "-"}</p>
          </div>
          <div className="col-span-2 flex items-center">
            <span className="font-serif text-lg font-bold text-white">{a.score_percentage}%</span>
          </div>
          <div className="col-span-2 flex items-center">
            <span className={`text-xs font-medium ${tierColors[a.tier] || "text-slate-400"}`}>
              {a.tier}
            </span>
          </div>
          <div className="col-span-2 flex items-center">
            <span className="text-xs text-slate-500">{formatDate(a.created_at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
